/**
 * Modal de recuperación cuando el usuario perdió su app de autenticación.
 *
 * Funciona SIN sesión (el usuario TOTP no puede completar el sign-in):
 *   Paso 1: email + password → callable requestMfaReset (respuesta genérica
 *           anti-enumeración; si los datos son correctos envía un OTP al correo).
 *   Paso 2: código → callable confirmMfaReset → des-enrola el TOTP.
 * Después el usuario inicia sesión solo con su password y, si el 2FA es
 * obligatorio para su cuenta, cae en el enrolamiento forzado.
 */

import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Form, Input, Modal, notification } from 'antd';
import { UserOutlined, LockOutlined, SafetyOutlined } from '@ant-design/icons';

import { mfa } from '../../actions/auth/mfa';

const FormItem = Form.Item;

export const ModalResetTotp = ({ visible, setVisible }) => {

    const dispatch = useDispatch();

    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1);
    const [email, setEmail] = useState(null);
    const [form] = Form.useForm();

    const onClose = () => {
        form.resetFields();
        setStep(1);
        setEmail(null);
        setVisible(false);
    };

    const onRequest = async values => {
        setLoading(true);
        try {
            await dispatch( mfa.requestReset( values.email, values.password ) );
            // La respuesta es genérica a propósito: no revela si la cuenta existe
            setEmail(values.email);
            setStep(2);
            form.resetFields();
        } catch ( error ) {
            notification.warning({
                message: 'No se pudo enviar el código',
                description: error?.message || 'Intente de nuevo en unos minutos',
                placement: 'topRight'
            });
        } finally {
            setLoading(false);
        }
    };

    const onConfirm = async values => {
        setLoading(true);
        try {
            await dispatch( mfa.confirmReset( email, values.code ) );
            notification.success({
                message: 'Autenticador eliminado',
                description: 'Inicie sesión con su contraseña y registre un autenticador nuevo',
                placement: 'topRight'
            });
            onClose();
        } catch ( error ) {
            notification.warning({
                message: 'No se pudo verificar',
                description: error?.message || 'Código incorrecto',
                placement: 'topRight'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            forceRender
            title = 'Recuperar acceso'
            cancelText = 'Cancelar'
            okText = { step === 1 ? 'Enviar código' : 'Confirmar' }
            maskClosable = { false }
            destroyOnHidden = { true }
            closable = { false }
            open = { visible }
            confirmLoading = { loading }
            onCancel = { onClose }
            onOk = { () => form.submit() } >

            { step === 1
                ? <Form form = { form } onFinish = { onRequest } >
                    <p> Confirme sus credenciales. Si son correctas, enviaremos un código de verificación a su correo para eliminar el autenticador perdido </p>
                    <FormItem name = "email"
                        rules = {[
                            { required: true, message: 'Debe ingresar una direccion de correo' },
                            { type: 'email', message: 'Debe ingresar una direccion de correo valida' }
                        ]} >
                        <Input type = "email" placeholder = "Email" prefix = { <UserOutlined style = {{ color: 'rgba(0,0,0,.25)' }} /> } />
                    </FormItem>
                    <FormItem name = "password"
                        rules = {[{ required: true, message: 'Debe ingresar su contraseña' }]} >
                        <Input type = "password" placeholder = "Password" prefix = { <LockOutlined style = {{ color: 'rgba(0,0,0,.25)' }} /> } />
                    </FormItem>
                </Form>
                : <Form form = { form } onFinish = { onConfirm } >
                    <p> Si los datos fueron correctos, recibirá un código de 6 dígitos en su correo. Ingréselo para eliminar el autenticador </p>
                    <FormItem name = "code"
                        rules = {[
                            { required: true, message: 'Debe ingresar el código' },
                            { pattern: /^[0-9]{6}$/, message: 'El código tiene 6 dígitos' }
                        ]} >
                        <Input
                            autoFocus
                            maxLength = { 6 }
                            inputMode = "numeric"
                            placeholder = "000000"
                            prefix = { <SafetyOutlined style = {{ color: 'rgba(0,0,0,.25)' }} /> } />
                    </FormItem>
                </Form>
            }
        </Modal>
    )
}
